import React, { useState } from 'react';
import { Info, Settings, BarChart3, ExternalLink, Monitor, RefreshCw, ChevronDown, ChevronUp, Cookie } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const CookiePolicyPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['1']);

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleResetPreferences = () => {
    localStorage.removeItem('cookie_consent');
    window.location.reload();
  };

  const sections: Section[] = [
    {
      id: '1',
      title: '1. Cosa sono i cookie',
      icon: <Info className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente (computer, tablet,
            smartphone) durante la navigazione. Vengono utilizzati per diverse finalità: far funzionare il sito,
            migliorare l'esperienza di navigazione, raccogliere informazioni statistiche.
          </p>
          <p>
            Oltre ai cookie, questa informativa riguarda anche tecnologie analoghe come il <strong>localStorage</strong> e
            il <strong>sessionStorage</strong> del browser, utilizzati per memorizzare informazioni necessarie al
            funzionamento del sito.
          </p>
        </div>
      ),
    },
    {
      id: '2',
      title: '2. Cookie tecnici necessari',
      icon: <Settings className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-4">
          <p>
            I cookie tecnici sono necessari per il corretto funzionamento del sito e <strong>non richiedono il
            consenso</strong> dell'utente (Provvedimento del Garante Privacy del 10 giugno 2021).
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Nome</th>
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Finalità</th>
                  <th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left font-semibold">Durata</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2 font-mono text-xs">token</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Autenticazione utente (JWT)</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Sessione / 2 ore</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2 font-mono text-xs">refreshToken</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Rinnovo automatico dell'autenticazione</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">7 giorni</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2 font-mono text-xs">theme</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Preferenza tema chiaro/scuro</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Persistente</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2 font-mono text-xs">cookie_consent</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">Memorizzazione della scelta sui cookie</td>
                  <td className="border border-gray-200 dark:border-gray-600 px-3 py-2">12 mesi</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
            Tutti i dati tecnici sopra elencati sono memorizzati nel localStorage del browser e non vengono trasmessi a server terzi.
          </p>
        </div>
      ),
    },
    {
      id: '3',
      title: '3. Cookie di analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>
            I cookie di analytics <strong>richiedono il consenso preventivo</strong> dell'utente e vengono installati
            solo dopo l'accettazione nel banner cookie.
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Google Analytics 4 (GA4)</h4>
            <ul className="space-y-1">
              <li><strong>Fornitore:</strong> Google LLC</li>
              <li><strong>Finalità:</strong> statistiche anonimizzate di navigazione (pagine visitate, durata delle visite, provenienza geografica aggregata)</li>
              <li><strong>IP anonimizzato:</strong> GA4 non registra l'indirizzo IP completo dell'utente</li>
              <li><strong>Dati raccolti:</strong> page views, eventi di interazione, tempo su pagina</li>
              <li><strong>Durata:</strong> secondo la configurazione di GA4 (default 14 mesi)</li>
              <li><strong>Privacy Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">policies.google.com/privacy</a></li>
              <li><strong>Opt-out GA:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">tools.google.com/dlpage/gaoptout</a></li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: '4',
      title: '4. Cookie di terze parti',
      icon: <ExternalLink className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>Alcuni servizi integrati nella Piattaforma possono installare cookie propri:</p>
          <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Stripe (pagamenti)</h4>
            <p>
              Durante il processo di pagamento, Stripe può installare cookie necessari per la sicurezza della
              transazione e la prevenzione delle frodi. Si tratta di <strong>cookie tecnici necessari</strong> per il
              servizio espressamente richiesto dall'utente, che non richiedono consenso preventivo.
            </p>
            <p className="mt-2">
              Privacy Stripe:{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                stripe.com/privacy
              </a>
            </p>
          </div>
        </div>
      ),
    },
    {
      id: '5',
      title: '5. Come gestire i cookie nel browser',
      icon: <Monitor className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>È possibile gestire, disabilitare o cancellare i cookie dalle impostazioni del proprio browser:</p>
          <ul className="space-y-2">
            <li><strong>Google Chrome:</strong> Impostazioni → Privacy e sicurezza → Cookie e altri dati dei siti</li>
            <li><strong>Mozilla Firefox:</strong> Impostazioni → Privacy e sicurezza → Cookie e dati dei siti web</li>
            <li><strong>Safari:</strong> Preferenze → Privacy → Gestisci dati siti web</li>
            <li><strong>Microsoft Edge:</strong> Impostazioni → Cookie e autorizzazioni sito → Gestisci ed elimina cookie</li>
          </ul>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Nota</p>
            <p>La disabilitazione dei cookie tecnici potrebbe impedire il corretto funzionamento del sito, in particolare l'accesso all'Area Riservata.</p>
          </div>
        </div>
      ),
    },
    {
      id: '6',
      title: '6. Modifica delle preferenze',
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-4">
          <p>
            Puoi modificare in qualsiasi momento la tua scelta sui cookie di analytics cliccando sul pulsante
            qui sotto. Il banner di consenso apparirà nuovamente e potrai aggiornare le tue preferenze.
          </p>
          <button
            onClick={handleResetPreferences}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Modifica preferenze cookie
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-custom py-8 legal-content">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl mb-4">
            <Cookie className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-base font-bold text-gray-900 dark:text-white mb-2">Cookie Policy</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Informativa sull'utilizzo dei cookie e tecnologie analoghe su studi.unasca.it
          </p>
        </div>

        {/* Sezioni Accordion */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-amber-600 dark:text-amber-400">{section.icon}</div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{section.title}</h2>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections.includes(section.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                  <div className="pt-4">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer aggiornamento */}
        <div className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
          <p>Ultimo aggiornamento: 4 aprile 2026</p>
          <p className="mt-1">
            Conforme al Provvedimento del Garante Privacy del 10 giugno 2021 (Linee guida cookie)
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
